import React, { useEffect, useMemo, useState } from 'react';

import { bind } from 'bind-event-listener';

import { cssMap } from '@atlaskit/css';
import { Box, Flex } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';

const styles = cssMap({
	header: {
		paddingTop: token('space.150'),
		paddingBottom: token('space.150'),
	},
	spacer: {
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
	},
	subtitle: {
		paddingBottom: token('space.050'),
	},
	apps: {
		paddingTop: token('space.050'),
		paddingBottom: token('space.100'),
	},
	footer: {
		paddingTop: token('space.100'),
	},
});

const IconSkeleton = (): React.JSX.Element => (
	<Skeleton width="32px" height="32px" borderRadius={token('radius.tile')} isShimmering />
);

const SubtitleSkeleton = (): React.JSX.Element => (
	<Flex direction="column" alignItems="center" gap="space.100" xcss={styles.subtitle}>
		<IconSkeleton />
		<Skeleton width="32px" height="4px" borderRadius={token('radius.full')} isShimmering />
	</Flex>
);

const SpacerSkeleton = (): React.JSX.Element => (
	<Box xcss={styles.spacer}>
		<Skeleton width="40px" height="1px" isShimmering />
	</Box>
);

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const getAppSkeletonCountForViewportHeight = (viewportHeight: number): number => {
	const reservedPx = 220; // header + footer + spacers + vertical padding (approx)
	const perItemPx = 52; // one app row height (approx)
	const availablePx = Math.max(0, viewportHeight - reservedPx);
	const estimatedCount = Math.floor(availablePx / perItemPx);
	return clamp(estimatedCount, 1, 9);
};

export const RibbonSkeleton = ({}: {}): React.JSX.Element => {
	const [appCount, setAppCount] = useState<number>(9);

	useEffect(() => {
		const update = () => setAppCount(getAppSkeletonCountForViewportHeight(window.innerHeight));
		update();

		const unbind = bind(window, { type: 'resize', listener: update });
		return unbind;
	}, []);

	//Render between 1 and 9 app skeletons based on the viewport height
	const appSkeletonRows = useMemo(
		() => Array.from({ length: appCount }, (_, index) => <SubtitleSkeleton key={index} />),
		[appCount],
	);

	return (
		<Flex direction="column" alignItems="center" testId="ribbon-skeleton">
			<Box xcss={styles.header}>
				<IconSkeleton />
			</Box>
			<SpacerSkeleton />
			<Flex
				direction="column"
				alignItems="center"
				justifyContent="center"
				gap="space.150"
				xcss={styles.apps}
			>
				{appSkeletonRows}
			</Flex>
			<SpacerSkeleton />
			<Box xcss={styles.footer}>
				<SubtitleSkeleton />
			</Box>
		</Flex>
	);
};
